import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import {
  Users, ShoppingBag, ShieldCheck, BarChart3, ArrowLeft, Crown, BookOpen,
  AlertTriangle, Wallet, Lightbulb, TrendingUp, FileText, MapPin, Gavel, Cog,
  Sparkles, Undo2, CalendarDays, ShieldAlert, Tag, Gift, AtSign, History
} from 'lucide-react';
import AiUsageTab from '../components/admin/AiUsageTab';
import SecurityTab from '../components/admin/SecurityTab';
import RefundAnalyticsTab from '../components/admin/RefundAnalyticsTab';
import EventsAdminTab from '../components/admin/EventsAdminTab';
import OverviewTab from '../components/admin/OverviewTab';
import VerificationsTab from '../components/admin/VerificationsTab';
import UsersTab from '../components/admin/UsersTab';
import ListingsTab from '../components/admin/ListingsTab';
import GrantsTab from '../components/admin/GrantsTab';
import CatalogApplicationsTab from '../components/admin/CatalogApplicationsTab';
import CatalogProposalsTab from '../components/admin/CatalogProposalsTab';
import ClassifiedCategoriesTab from '../components/admin/ClassifiedCategoriesTab';
import CommissionsTab from '../components/admin/CommissionsTab';
import PaymentsTab from '../components/admin/PaymentsTab';
import BlogTab from '../components/admin/BlogTab';
import RegionsTab from '../components/admin/RegionsTab';
import DangerZoneTab from '../components/admin/DangerZoneTab';
import DisputesTab from '../components/admin/DisputesTab';
import SystemTab from '../components/admin/SystemTab';
import InvestorsTab from '../components/admin/InvestorsTab';
import ReferralsTab from '../components/admin/ReferralsTab';
import NicknamesTab from '../components/admin/NicknamesTab';
import AdminAuditTab from '../components/admin/AdminAuditTab';

const TABS = [
  { id: 'overview', icon: BarChart3 },
  { id: 'verifications', icon: ShieldCheck },
  { id: 'disputes', icon: Gavel },
  { id: 'users', icon: Users },
  { id: 'nicknames', icon: AtSign },
  { id: 'listings', icon: ShoppingBag },
  { id: 'catalogs', icon: BookOpen },
  { id: 'proposals', icon: Lightbulb },
  { id: 'classified-cats', icon: Tag },
  { id: 'grants', icon: Crown },
  { id: 'blog', icon: FileText },
  { id: 'regions', icon: MapPin },
  { id: 'events', icon: CalendarDays },
  { id: 'payments', icon: Wallet },
  { id: 'commissions', icon: TrendingUp },
  { id: 'referrals', icon: Gift },
  { id: 'investors', icon: Sparkles },
  { id: 'refunds', icon: Undo2 },
  { id: 'ai', icon: Sparkles },
  { id: 'security', icon: ShieldAlert },
  { id: 'system', icon: Cog },
  { id: 'audit', icon: History },
  { id: 'danger', icon: AlertTriangle },
];

const TAB_LABELS = {
  lv: { overview: 'Pārskats', verifications: 'Verifikācijas', disputes: 'Strīdi', users: 'Lietotāji', nicknames: 'Iesaukas', listings: 'Sludinājumi', catalogs: 'Katalogi', proposals: 'Ierosinājumi', 'classified-cats': 'Slud. kategorijas', grants: 'Piešķirt', blog: 'Blogs', regions: 'Reģioni', events: 'Pasākumi', payments: 'Maksājumi', commissions: 'Komisijas', referrals: 'Referāļi', investors: 'Investori', refunds: 'Atmaksas', ai: 'AI', security: 'Drošība', system: 'Sistēma', audit: 'Admin žurnāls', danger: 'Datu tīrīšana' },
  en: { overview: 'Overview', verifications: 'Verifications', disputes: 'Disputes', users: 'Users', nicknames: 'Nicknames', listings: 'Listings', catalogs: 'Catalogs', proposals: 'Proposals', 'classified-cats': 'Classified cats', grants: 'Grant', blog: 'Blog', regions: 'Regions', events: 'Events', payments: 'Payments', commissions: 'Commissions', referrals: 'Referrals', investors: 'Investors', refunds: 'Refunds', ai: 'AI', security: 'Security', system: 'System', audit: 'Admin log', danger: 'Data reset' }
};

// Per-tab icon lookup, derived from the flat TABS list above.
const TAB_ICON = Object.fromEntries(TABS.map((t) => [t.id, t.icon]));

// Grouped navigation — the 16 admin sections folded into 6 professional
// domains (a top-level group row + a contextual sub-tab row). Every content
// section keeps its original `data-testid="admin-tab-{id}"`.
const TAB_GROUPS = [
  { id: 'dashboard',  icon: BarChart3,  tabs: ['overview'] },
  { id: 'moderation', icon: ShieldCheck, tabs: ['verifications', 'disputes', 'listings'] },
  { id: 'people',     icon: Users,      tabs: ['users', 'nicknames', 'grants'] },
  { id: 'content',    icon: BookOpen,   tabs: ['catalogs', 'proposals', 'classified-cats', 'blog', 'regions', 'events'] },
  { id: 'finance',    icon: Wallet,     tabs: ['payments', 'commissions', 'referrals', 'investors', 'refunds'] },
  { id: 'system',     icon: Cog,        tabs: ['ai', 'security', 'system', 'audit', 'danger'] },
];

const GROUP_LABELS = {
  lv: { dashboard: 'Pārskats', moderation: 'Moderācija', people: 'Lietotāji', content: 'Saturs', finance: 'Finanses', system: 'Sistēma' },
  en: { dashboard: 'Dashboard', moderation: 'Moderation', people: 'People', content: 'Content', finance: 'Finance', system: 'System' },
};

export default function AdminPage() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const validTabs = TABS.map((t) => t.id);
  const urlTab = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(validTabs.includes(urlTab) ? urlTab : 'overview');

  // Persist the active section to the URL (?tab=) so a reload or shared link
  // reopens the same view instead of always resetting to the overview.
  const selectTab = (t) => {
    setActiveTab(t);
    const next = new URLSearchParams(searchParams);
    next.set('tab', t);
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    if (user.role !== 'admin') { navigate('/'); return; }
  }, [user]);

  // Reflect browser back/forward URL changes into state.
  useEffect(() => {
    const t = searchParams.get('tab');
    if (t && validTabs.includes(t)) setActiveTab(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  if (!user || user.role !== 'admin') return null;

  const activeGroup = TAB_GROUPS.find((g) => g.tabs.includes(activeTab)) || TAB_GROUPS[0];

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-3 mb-8">
          <button onClick={() => navigate('/')} className="text-[#5C5C5C] hover:text-[#121212]" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="font-outfit text-3xl font-bold text-[#121212]">
            {language === 'lv' ? 'Administrācijas panelis' : 'Admin Panel'}
          </h1>
        </div>

        <div className="flex gap-6 lg:gap-8">
          {/* Desktop sidebar — domain groups (CRM layout) */}
          <aside className="hidden lg:block w-56 shrink-0" data-testid="admin-sidebar">
            <nav className="sticky top-8 space-y-1">
              {TAB_GROUPS.map((g) => {
                const Icon = g.icon;
                const on = g.id === activeGroup.id;
                return (
                  <button key={g.id} onClick={() => selectTab(g.tabs[0])}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors ${on ? 'bg-[#121212] text-white' : 'text-[#5C5C5C] hover:bg-white hover:text-[#121212]'}`}
                    data-testid={`admin-group-${g.id}`}>
                    <Icon className="w-4 h-4 shrink-0" /> {GROUP_LABELS[language]?.[g.id] || g.id}
                  </button>
                );
              })}
            </nav>
          </aside>

          {/* Content */}
          <div className="flex-1 min-w-0 pb-24 lg:pb-0">
            {/* Sub-tabs for the active group (shown on all sizes) */}
            {activeGroup.tabs.length > 1 && (
              <div className="flex gap-2 mb-6 overflow-x-auto pb-1" data-testid="admin-subtabs">
                {activeGroup.tabs.map((tabId) => {
                  const Icon = TAB_ICON[tabId];
                  return (
                    <button key={tabId} onClick={() => selectTab(tabId)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${activeTab === tabId ? 'bg-[#D97736] text-white' : 'bg-white border border-[#E5E4E0] text-[#5C5C5C] hover:border-[#D97736]'}`}
                      data-testid={`admin-tab-${tabId}`}>
                      {Icon && <Icon className="w-4 h-4" />}
                      {TAB_LABELS[language]?.[tabId] || tabId}
                    </button>
                  );
                })}
              </div>
            )}

            {activeTab === 'overview' && <OverviewTab language={language} />}
            {activeTab === 'verifications' && <VerificationsTab language={language} />}
            {activeTab === 'disputes' && <DisputesTab language={language} />}
            {activeTab === 'users' && <UsersTab language={language} />}
            {activeTab === 'nicknames' && <NicknamesTab language={language} />}
            {activeTab === 'listings' && <ListingsTab language={language} />}
            {activeTab === 'catalogs' && <CatalogApplicationsTab language={language} />}
            {activeTab === 'proposals' && <CatalogProposalsTab language={language} />}
            {activeTab === 'classified-cats' && <ClassifiedCategoriesTab language={language} />}
            {activeTab === 'grants' && <GrantsTab language={language} />}
            {activeTab === 'blog' && <BlogTab language={language} />}
            {activeTab === 'regions' && <RegionsTab language={language} />}
            {activeTab === 'events' && <EventsAdminTab language={language} />}
            {activeTab === 'payments' && <PaymentsTab language={language} />}
            {activeTab === 'commissions' && <CommissionsTab language={language} />}
            {activeTab === 'referrals' && <ReferralsTab language={language} />}
            {activeTab === 'investors' && <InvestorsTab language={language} />}
            {activeTab === 'refunds' && <RefundAnalyticsTab language={language} />}
            {activeTab === 'ai' && <AiUsageTab language={language} />}
            {activeTab === 'security' && <SecurityTab language={language} />}
            {activeTab === 'system' && <SystemTab language={language} />}
            {activeTab === 'audit' && <AdminAuditTab language={language} />}
            {activeTab === 'danger' && <DangerZoneTab language={language} />}
          </div>
        </div>
      </div>

      {/* Mobile bottom bar — domain groups */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-50 bg-white/95 backdrop-blur-xl border-t border-[#E5E4E0]" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }} data-testid="admin-bottom-nav">
        <div className="flex items-stretch justify-around">
          {TAB_GROUPS.map((g) => {
            const Icon = g.icon;
            const on = g.id === activeGroup.id;
            return (
              <button key={g.id} onClick={() => selectTab(g.tabs[0])}
                className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-2 min-h-[54px] transition-colors active:scale-95 ${on ? 'text-[#D97736]' : 'text-[#9CA3AF]'}`}
                data-testid={`admin-bottomnav-${g.id}`}>
                <Icon className="w-[21px] h-[21px]" strokeWidth={on ? 2.4 : 1.9} />
                <span className="text-[10px] font-medium leading-none">{GROUP_LABELS[language]?.[g.id] || g.id}</span>
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
